// Yet another duplication of price calculation logic in the app stack.
// The booking summary, the server charge, and now this estimate all compute
// totals independently. Ideally this would be a single shared calculation,
// but that refactor is out of scope. This helper exists so the upsell widget
// can at least mirror what the booking summary shows for tickets + fees
// before a charge object exists.

export interface BookingEstimateParams {
	pricingTotal: number; // IPrice.total (cost + tax per ticket)
	pricingCost: number; // IPrice.cost (base cost per ticket, for private session calc)
	pricingTax: number; // IPrice.tax (tax per ticket, for private session calc)
	playerCount: number;
	perPersonPricing: boolean;
	feeAmount?: number;
	feePercent?: number;
	// Private session upgrade
	selectedPrivateUpgrade?: boolean;
	privacyTypeId?: number;
	privacyChargePercentage?: number;
	maximumSlots?: number;
	remainingSlots?: number;
	// Discount — mirrors Invoice.UpdatePromoDiscount behaviour
	discountRate: number;
	isPerSlot: boolean;
}

export interface BookingEstimateResult {
	total: number; // full price before discount (strikethrough)
	discountedTotal: number; // price after promo discount
}

export function calculateBookingEstimate(params: BookingEstimateParams): BookingEstimateResult {
	// 1. Ticket total
	let ticketTotal = params.pricingTotal;
	if (params.perPersonPricing) {
		ticketTotal *= params.playerCount;
	}

	// 2. Private session upgrade cost (mirrors booking-summary.tsx)
	let privateSessionTotal = 0;
	if (params.selectedPrivateUpgrade
		&& params.privacyTypeId === 2
		&& params.maximumSlots !== undefined
		&& params.remainingSlots !== undefined
		&& params.maximumSlots === params.remainingSlots) {
		const missingPlayers = params.maximumSlots - params.playerCount;
		const privateCost = (missingPlayers * params.pricingCost * (params.privacyChargePercentage ?? 0)) / 100;
		const privateTax = (missingPlayers * params.pricingTax * (params.privacyChargePercentage ?? 0)) / 100;
		privateSessionTotal = privateCost + privateTax;
	}

	// 3. Discount — base depends on isPerSlot (mirrors Invoice.UpdatePromoDiscount)
	// isPerSlot=true:  discount applies to ticket total only
	// isPerSlot=false: discount applies to ticket total + private session fee
	// Booking fees are never part of the discount base.
	const discountBase = params.isPerSlot ? ticketTotal : (ticketTotal + privateSessionTotal);
	const discountAmount = discountBase * params.discountRate;

	const subtotalBeforeDiscount = ticketTotal + privateSessionTotal;
	const subtotalAfterDiscount = subtotalBeforeDiscount - discountAmount;

	// 4. Booking fees (calculated on respective subtotals)
	let feeBeforeDiscount = 0;
	let feeAfterDiscount = 0;
	if (params.feeAmount !== undefined && params.feePercent !== undefined) {
		feeBeforeDiscount = params.feeAmount + (subtotalBeforeDiscount * (params.feePercent / 100));
		feeAfterDiscount = params.feeAmount + (subtotalAfterDiscount * (params.feePercent / 100));
	}

	return {
		total: subtotalBeforeDiscount + feeBeforeDiscount,
		discountedTotal: subtotalAfterDiscount + feeAfterDiscount,
	};
}
